'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@/shared/navigation'
import { fetchTodayAttendance } from '@/features/kitchen/api/getTodayAttendance'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import {
  mapAttendanceRecordsFromApi,
  mapStaffFromApi,
} from '@/features/kitchen/api/mapAttendanceFromApi'
import { submitAttendance, type AttendanceAction } from '@/features/kitchen/api/submitAttendance'
import { uploadPhoto } from '@/shared/api/uploadPhoto'
import {
  getStaffRoleMeta,
  KITCHEN_STAFF_ROSTER,
  type KitchenStaffMember,
} from '@/features/kitchen/kitchenStaff'
import { CollapsibleCategoryBlock } from '@/features/kitchen/CollapsibleCategoryBlock'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { SkeletonList } from '@/shared/components/Skeleton'
import { SelfieCapture } from '@/shared/components/SelfieCapture'
import { Toast } from '@/shared/components/Toast'
import {
  loadAttendanceDayRecords,
  saveAttendanceDayRecords,
  todayDateKey,
  type AttendanceEventRecord,
  type StaffDayAttendanceRecord,
} from '@/features/kitchen/storage/attendanceStorage'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import {
  getKitchenSession,
  updateKitchenSessionAuthority,
} from '@/shared/auth/sessionStorage'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'

type AttendanceEvent = AttendanceEventRecord
type StaffDayAttendance = StaffDayAttendanceRecord

type FlowStep = 'home' | 'selfie' | 'confirm'

type StaffStatus = 'belum' | 'masuk' | 'selesai'

type StaffRoleGroup = {
  roleLabel: string
  sortOrder: number
  staff: KitchenStaffMember[]
}

function formatTodayLabel(): string {
  return new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date())
}

function formatTimeLabel(iso: string): string {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function formatNowClock(): string {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date())
}

function getStaffStatus(record: StaffDayAttendance | undefined): StaffStatus {
  if (!record?.checkIn) return 'belum'
  if (!record.checkOut) return 'masuk'
  return 'selesai'
}

function statusLabel(status: StaffStatus): string {
  if (status === 'belum') return kitchenTerms.attendance.notYet
  if (status === 'masuk') return kitchenTerms.attendance.onShift
  return kitchenTerms.attendance.finished
}

function statusBadgeClass(status: StaffStatus): string {
  if (status === 'belum') return 'badge-draft'
  if (status === 'masuk') return 'badge-warning'
  return 'badge-success'
}

function statusSortOrder(status: StaffStatus): number {
  if (status === 'belum') return 0
  if (status === 'masuk') return 1
  return 2
}

function roleSortOrder(staff: KitchenStaffMember): number {
  if (staff.role === 'HEAD_KITCHEN') return 0
  if (staff.roleLabel === 'Koki') return 1
  return 2
}

function isOnTimeCheckIn(iso: string, shiftStart: string): boolean {
  const event = new Date(iso)
  const start = new Date(event)
  const [hour, minute] = shiftStart.split(':').map(Number)
  start.setHours(hour, minute, 0, 0)
  return event.getTime() <= start.getTime()
}

function isPastTeamShiftStart(now: Date, shiftStart: string): boolean {
  const start = new Date(now)
  const [hour, minute] = shiftStart.split(':').map(Number)
  start.setHours(hour, minute, 0, 0)
  return now.getTime() > start.getTime()
}

function formatDuration(checkIn: string, checkOut: string): string {
  const ms = new Date(checkOut).getTime() - new Date(checkIn).getTime()
  const totalMinutes = Math.max(0, Math.floor(ms / 60000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return kitchenTerms.attendance.duration(hours, minutes)
}

function revokePhotoUrl(url: string | null) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

function groupStaffByRole(
  roster: KitchenStaffMember[],
  records: Record<string, StaffDayAttendance>,
): StaffRoleGroup[] {
  const map = new Map<string, StaffRoleGroup>()

  for (const staff of roster) {
    const key = staff.roleLabel.trim() || 'Staff'
    let group = map.get(key)
    if (!group) {
      group = {
        roleLabel: key,
        sortOrder: roleSortOrder(staff),
        staff: [],
      }
      map.set(key, group)
    }
    group.staff.push(staff)
  }

  return [...map.values()]
    .map((group) => ({
      ...group,
      staff: [...group.staff].sort((a, b) => {
        const statusDiff =
          statusSortOrder(getStaffStatus(records[a.id])) -
          statusSortOrder(getStaffStatus(records[b.id]))
        if (statusDiff !== 0) return statusDiff
        return a.name.localeCompare(b.name, 'id')
      }),
    }))
    .sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
      return a.roleLabel.localeCompare(b.roleLabel, 'id')
    })
}

function StaffRoleChip({ staff }: { staff: KitchenStaffMember }) {
  const meta = getStaffRoleMeta(staff)
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-label-md font-semibold uppercase tracking-wide text-[10px] px-2 py-0.5 ${meta.chipClass}`}
    >
      <span className="material-symbols-outlined text-sm leading-none">{meta.icon}</span>
      {staff.roleLabel}
    </span>
  )
}

function AttendanceFlowStepper({
  current,
  pinOpen,
}: {
  current: 1 | 2 | 3
  pinOpen: boolean
}) {
  const steps = kitchenTerms.attendance.flowSteps.map((label) => ({ label }))
  return <FlowStepper steps={steps} activeStep={pinOpen ? 4 : current} />
}

function AttendanceSelfieThumb({ src, alt }: { src: string | null | undefined; alt: string }) {
  if (!src) {
    return (
      <div className="w-full h-full bg-surface-variant flex items-center justify-center">
        <span className="material-symbols-outlined text-on-surface-variant text-lg" aria-hidden>
          person
        </span>
      </div>
    )
  }
  return <img src={src} alt={alt} className="w-full h-full object-cover" />
}

function StaffAttendanceCard({
  staff,
  record,
  onAction,
  muted = false,
}: {
  staff: KitchenStaffMember
  record: StaffDayAttendance | undefined
  onAction: (staff: KitchenStaffMember) => void
  muted?: boolean
}) {
  const status = getStaffStatus(record)
  const canAct = status !== 'selesai'

  return (
    <div className={`card space-y-3 ${muted ? 'opacity-75' : ''}`}>
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="text-body-lg font-bold text-on-surface min-w-0 break-words flex-1">
            {staff.name || '—'}
          </p>
          <span className={`shrink-0 ${statusBadgeClass(status)}`}>{statusLabel(status)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StaffRoleChip staff={staff} />
          <span className="text-label-md text-on-surface-variant">
            {staff.shiftStart} – {staff.shiftEnd}
          </span>
        </div>
      </div>

      {status === 'masuk' && record?.checkIn && (
        <div className="flex items-center gap-3 rounded-lg bg-surface-container p-3">
          <div className="w-11 h-11 rounded-lg overflow-hidden border border-outline-variant shrink-0">
            <AttendanceSelfieThumb
              src={record.checkIn.photoUrl}
              alt={kitchenTerms.attendance.selfie.alt(staff.name)}
            />
          </div>
          <div className="text-body-sm">
            <p className="text-on-surface-variant">{kitchenTerms.attendance.checkIn}</p>
            <p className="font-mono font-bold text-body-lg">{record.checkIn.timeLabel}</p>
            <p
              className={`text-label-md font-semibold ${
                record.checkIn.onTime ? 'text-status-success' : 'text-status-warning'
              }`}
            >
              {record.checkIn.onTime
                ? kitchenTerms.attendance.onTime
                : kitchenTerms.attendance.late}
            </p>
          </div>
        </div>
      )}

      {status === 'selesai' && record?.checkIn && record.checkOut && (
        <div className="rounded-lg bg-surface-container p-3 text-body-sm space-y-2">
          <div className="flex gap-2">
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-outline-variant">
              <AttendanceSelfieThumb
                src={record.checkIn.photoUrl}
                alt={kitchenTerms.attendance.selfie.checkInThumb}
              />
            </div>
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-outline-variant">
              <AttendanceSelfieThumb
                src={record.checkOut.photoUrl}
                alt={kitchenTerms.attendance.selfie.checkOutThumb}
              />
            </div>
          </div>
          <p>
            <span className="text-on-surface-variant">{kitchenTerms.attendance.checkIn}</span>{' '}
            <strong className="font-mono">{record.checkIn.timeLabel}</strong>
            <span className="mx-2">·</span>
            <span className="text-on-surface-variant">{kitchenTerms.attendance.checkOut}</span>{' '}
            <strong className="font-mono">{record.checkOut.timeLabel}</strong>
          </p>
          <p className="text-on-surface-variant">
            <strong className="text-on-surface">
              {formatDuration(record.checkIn.at, record.checkOut.at)}
            </strong>
          </p>
        </div>
      )}

      {canAct && (
        <button
          type="button"
          className={
            status === 'belum'
              ? 'btn-primary w-full text-center'
              : 'w-full min-h-[44px] rounded-lg font-bold px-4 py-2 border-2 border-status-warning text-status-warning bg-status-warning-bg/30'
          }
          onClick={() => onAction(staff)}
        >
          {status === 'belum'
            ? kitchenTerms.attendance.checkIn
            : kitchenTerms.attendance.checkOutAction}
        </button>
      )}
    </div>
  )
}

export function KitchenAttendancePage() {
  const useApi = shouldUseKitchenApi()
  const device = getKitchenDevice()
  const date = todayDateKey()
  const teamShiftStart = kitchenTerms.attendance.teamShiftStart

  const [roster, setRoster] = useState<KitchenStaffMember[]>(() =>
    useApi ? [] : KITCHEN_STAFF_ROSTER,
  )
  const [records, setRecords] = useState<Record<string, StaffDayAttendance>>(() =>
    useApi ? {} : loadAttendanceDayRecords(date),
  )
  const [isLoading, setIsLoading] = useState(useApi)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [step, setStep] = useState<FlowStep>('home')
  const [selectedStaff, setSelectedStaff] = useState<KitchenStaffMember | null>(null)
  const [pendingAction, setPendingAction] = useState<AttendanceAction | null>(null)
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null)
  const [selfieError, setSelfieError] = useState<string | null>(null)
  const [isPinOpen, setIsPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [nowClock, setNowClock] = useState(formatNowClock)

  const reload = useCallback(() => {
    if (!useApi) {
      setRoster(KITCHEN_STAFF_ROSTER)
      setRecords(loadAttendanceDayRecords(date))
      setIsLoading(false)
      setLoadError(null)
      return () => undefined
    }
    if (!device?.deviceId) {
      setRoster([])
      setRecords({})
      setIsLoading(false)
      return () => undefined
    }

    let cancelled = false
    setIsLoading(true)
    setLoadError(null)

    fetchTodayAttendance(device.deviceId)
      .then((data) => {
        if (cancelled) return
        setRoster(mapStaffFromApi(data.staff))
        setRecords(mapAttendanceRecordsFromApi(data.staff, date))
      })
      .catch(() => {
        if (!cancelled) {
          setRoster([])
          setRecords({})
          setLoadError(kitchenTerms.attendance.loadError)
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, date])

  useEffect(() => {
    return reload()
  }, [reload, refreshKey])

  const kitchenName = device?.kitchenName ?? 'Dapur'
  const todayLabel = useMemo(() => formatTodayLabel(), [])

  useEffect(() => {
    const timer = window.setInterval(() => setNowClock(formatNowClock()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  const roleGroups = useMemo(
    () => groupStaffByRole(roster, records),
    [records, roster],
  )

  const notCheckedInStaff = useMemo(
    () => roster.filter((member) => !records[member.id]?.checkIn),
    [records, roster],
  )

  const checkedInCount = roster.filter((member) => records[member.id]?.checkIn).length
  const finishedCount = roster.filter((member) => records[member.id]?.checkOut).length
  const progressPct = roster.length > 0 ? Math.round((checkedInCount / roster.length) * 100) : 0
  const missingNames = notCheckedInStaff.map((s) => s.name.split(' ')[0]).join(', ')
  const showMissingAlert =
    isPastTeamShiftStart(new Date(), teamShiftStart) && notCheckedInStaff.length > 0

  const flowStepNumber: 1 | 2 | 3 =
    step === 'home' ? 1 : step === 'selfie' ? 2 : 3

  function resetFlow() {
    revokePhotoUrl(selfiePreview)
    setSelfiePreview(null)
    setSelfieError(null)
    setPendingAction(null)
    setSelectedStaff(null)
    setIsPinOpen(false)
    setStep('home')
  }

  function startAction(staff: KitchenStaffMember, action: AttendanceAction) {
    setSelectedStaff(staff)
    setPendingAction(action)
    setSelfieError(null)
    setSelfiePreview(null)
    setStep('selfie')
  }

  function handleStaffAction(staff: KitchenStaffMember) {
    const status = getStaffStatus(records[staff.id])
    if (status === 'belum') startAction(staff, 'check_in')
    else if (status === 'masuk') startAction(staff, 'check_out')
  }

  function handlePhotoChange(url: string | null) {
    if (selfiePreview && url !== selfiePreview) {
      revokePhotoUrl(selfiePreview)
    }
    setSelfiePreview(url)
    setSelfieError(null)
  }

  function openConfirmStep() {
    if (!selfiePreview) {
      setSelfieError(kitchenTerms.attendance.selfie.required)
      return
    }
    setStep('confirm')
  }

  async function confirmAttendance(pin: string) {
    if (!device || !pendingAction || !selfiePreview || !selectedStaff) {
      throw new Error(kitchenTerms.attendance.errors.incomplete)
    }

    const clientEventId = crypto.randomUUID()
    const uploaded = await uploadPhoto(selfiePreview, 'attendance', { clientEventId })

    const result = await submitAttendance({
      deviceId: device.deviceId,
      userId: selectedStaff.id,
      pin,
      action: pendingAction,
      photoUrl: uploaded.key,
    })

    const event: AttendanceEvent = {
      at: result.recorded_at,
      timeLabel: formatTimeLabel(result.recorded_at),
      photoUrl: uploaded.url,
      onTime:
        pendingAction === 'check_in'
          ? (result.on_time ?? isOnTimeCheckIn(result.recorded_at, selectedStaff.shiftStart))
          : undefined,
    }

    const existing = records[selectedStaff.id]
    const nextRecord: StaffDayAttendance = {
      userId: selectedStaff.id,
      date,
      checkIn: existing?.checkIn,
      checkOut: existing?.checkOut,
    }

    if (pendingAction === 'check_in') nextRecord.checkIn = event
    else nextRecord.checkOut = event

    const nextRecords = { ...records, [selectedStaff.id]: nextRecord }
    if (!useApi) {
      saveAttendanceDayRecords(date, nextRecords)
    }
    setRecords(nextRecords)

    const session = getKitchenSession()
    if (result.authority || typeof result.head_present === 'boolean') {
      updateKitchenSessionAuthority(
        result.authority ?? session?.authority ?? 'attendance_only',
        result.head_present ?? session?.head_present ?? false,
      )
    } else if (session?.role === 'ASSISTANT_HEAD_KITCHEN') {
      const headPresent = roster.some(
        (member) => member.role === 'HEAD_KITCHEN' && Boolean(nextRecords[member.id]?.checkIn),
      )
      updateKitchenSessionAuthority(headPresent ? 'attendance_only' : 'full', headPresent)
    }
    setIsPinOpen(false)

    const staffName = selectedStaff.name
    const syncQueued = consumeLastSyncQueued()
    const message =
      pendingAction === 'check_in'
        ? kitchenTerms.attendance.toast.checkIn(
            staffName,
            kitchenTerms.attendance.checkInVerb,
            event.timeLabel,
            Boolean(event.onTime),
            syncQueued,
          )
        : kitchenTerms.attendance.toast.checkOut(
            staffName,
            kitchenTerms.attendance.checkOutVerb,
            event.timeLabel,
            syncQueued,
          )

    resetFlow()
    showToast(message)
  }

  const actionLabel =
    pendingAction === 'check_out'
      ? kitchenTerms.attendance.checkOutVerb
      : kitchenTerms.attendance.checkInVerb
  const actionTitle =
    pendingAction === 'check_out'
      ? kitchenTerms.attendance.checkOut
      : kitchenTerms.attendance.checkIn

  return (
    <section className="space-y-4">
      {step === 'home' && (
        <>
          <div className="card space-y-3 relative">
            <button
              type="button"
              className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
              onClick={() => setRefreshKey((k) => k + 1)}
              aria-label={kitchenTerms.attendance.refreshAria}
              title={kitchenTerms.attendance.refreshAria}
            >
              <span className="material-symbols-outlined">refresh</span>
            </button>

            <div className="flex items-start gap-3 pr-12">
              <div className="w-12 h-12 rounded-full bg-primary-container text-on-primary-container flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-2xl">groups</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-headline-md text-primary break-words">
                  {kitchenTerms.attendance.title}
                </p>
                <p className="text-body-sm text-on-surface-variant capitalize">
                  {kitchenTerms.attendance.subtitle(kitchenName, todayLabel)}
                </p>
                <p className="text-body-sm text-on-surface-variant mt-1">
                  {kitchenTerms.attendance.nowClock(nowClock, teamShiftStart)}
                </p>
              </div>
            </div>

            <p className="text-body-sm text-on-surface-variant bg-surface-container rounded-lg px-3 py-2 border border-outline-variant">
              {kitchenTerms.attendance.hint}
            </p>

            {roster.length > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between gap-2 text-body-sm">
                  <span className="text-on-surface-variant shrink-0">
                    {kitchenTerms.attendance.checkInTeam}
                  </span>
                  <span className="font-semibold text-right">
                    {kitchenTerms.attendance.progress(checkedInCount, roster.length)}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-surface-container overflow-hidden">
                  <div
                    className="h-full bg-status-success transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <p className="text-body-sm text-on-surface-variant">
                  {kitchenTerms.attendance.finishedCount(finishedCount)}
                  {notCheckedInStaff.length > 0 && (
                    <>
                      {' '}
                      · {kitchenTerms.attendance.notCheckedInNames(missingNames)}
                    </>
                  )}
                </p>
              </div>
            )}

            {showMissingAlert && (
              <div
                role="alert"
                className="rounded-lg border border-status-warning/40 bg-status-warning-bg/50 px-3 py-2 text-body-sm text-status-warning"
              >
                <strong>{kitchenTerms.attendance.missingAlert(notCheckedInStaff.length)}</strong>
              </div>
            )}
          </div>

          {isLoading && <SkeletonList count={3} label={kitchenTerms.attendance.loading} />}

          {loadError && (
            <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
              <span className="text-body-sm">{loadError}</span>
              <button
                type="button"
                className="btn-outline min-h-[36px] text-body-sm"
                onClick={() => setRefreshKey((k) => k + 1)}
              >
                {kitchenTerms.attendance.retry}
              </button>
            </div>
          )}

          {!isLoading && !loadError && roster.length === 0 && (
            <EmptyState
              icon="groups"
              title={kitchenTerms.attendance.empty.title}
              description={kitchenTerms.attendance.empty.description}
            />
          )}

          {!isLoading && !loadError && roleGroups.length > 0 && (
            <div className="space-y-2">
              {roleGroups.map((group) => {
                const checkedIn = group.staff.filter((s) => records[s.id]?.checkIn).length
                const defaultOpen = group.staff.some(
                  (s) => getStaffStatus(records[s.id]) !== 'selesai',
                )
                return (
                  <CollapsibleCategoryBlock
                    key={group.roleLabel}
                    categoryName={group.roleLabel}
                    itemCount={group.staff.length}
                    itemCountLabel="orang"
                    defaultOpen={defaultOpen}
                    summary={kitchenTerms.attendance.roleGroupSummary(checkedIn, group.staff.length)}
                  >
                    <div className="space-y-2 p-2">
                      {group.staff.map((staff) => (
                        <StaffAttendanceCard
                          key={staff.id}
                          staff={staff}
                          record={records[staff.id]}
                          onAction={handleStaffAction}
                          muted={getStaffStatus(records[staff.id]) === 'selesai'}
                        />
                      ))}
                    </div>
                  </CollapsibleCategoryBlock>
                )
              })}
            </div>
          )}

          <Link href="/kitchen/home" className="btn-outline block text-center min-h-[44px]">
            {kitchenTerms.attendance.backHome}
          </Link>
        </>
      )}

      {step !== 'home' && selectedStaff && pendingAction && (
        <div className="card space-y-3">
          <AttendanceFlowStepper current={flowStepNumber} pinOpen={isPinOpen} />

          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div className="space-y-1">
              <p className="text-headline-md text-primary">{actionTitle}</p>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-body-sm text-on-surface-variant">{selectedStaff.name}</p>
                <StaffRoleChip staff={selectedStaff} />
              </div>
            </div>
            <button type="button" className="btn-outline min-h-[44px]" onClick={resetFlow}>
              {kitchenTerms.attendance.flow.cancel}
            </button>
          </div>
        </div>
      )}

      {step === 'selfie' && pendingAction && selectedStaff && (
        <div className="card space-y-3">
          <SelfieCapture
            photoUrl={selfiePreview}
            onPhotoChange={handlePhotoChange}
            staffName={selectedStaff.name}
          />

          {selfieError && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{selfieError}</span>
            </div>
          )}

          <button
            type="button"
            className="btn-primary w-full min-h-[44px] disabled:opacity-50"
            disabled={!selfiePreview}
            onClick={openConfirmStep}
          >
            {kitchenTerms.attendance.flow.continueConfirm}
          </button>
        </div>
      )}

      {step === 'confirm' && pendingAction && selectedStaff && selfiePreview && (
        <div className="card space-y-3">
          <p className="text-body-sm text-on-surface-variant text-center">
            {kitchenTerms.attendance.flow.confirmHint}
          </p>

          <div className="rounded-xl border border-outline-variant overflow-hidden">
            <img
              src={selfiePreview}
              alt={kitchenTerms.attendance.selfie.confirmAlt(selectedStaff.name)}
              className="w-full aspect-[4/3] object-cover"
            />
          </div>

          <div className="rounded-lg bg-surface-container p-3 space-y-2 text-body-sm">
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">{kitchenTerms.attendance.confirm.name}</span>
              <strong className="text-on-surface">{selectedStaff.name}</strong>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">{kitchenTerms.attendance.confirm.role}</span>
              <span>{selectedStaff.roleLabel}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">{kitchenTerms.attendance.confirm.action}</span>
              <strong className="capitalize">{actionLabel}</strong>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">{kitchenTerms.attendance.confirm.time}</span>
              <strong className="font-mono">{nowClock}</strong>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="btn-primary w-full min-h-[44px]"
              onClick={() => setIsPinOpen(true)}
            >
              {kitchenTerms.attendance.flow.continuePin}
            </button>
            <button
              type="button"
              className="btn-outline w-full min-h-[44px]"
              onClick={() => setStep('selfie')}
            >
              {kitchenTerms.attendance.flow.retakeSelfie}
            </button>
          </div>

          <p className="text-label-md text-on-surface-variant text-center">
            {kitchenTerms.attendance.flow.pinStaffHint(selectedStaff.name)}
          </p>
        </div>
      )}

      <PinKeypadModal
        open={isPinOpen}
        title={kitchenTerms.attendance.pin.title(actionLabel)}
        description={kitchenTerms.attendance.pin.description(
          selectedStaff?.name ?? 'staff',
          actionLabel,
        )}
        confirmLabel={kitchenTerms.attendance.pin.confirm}
        onClose={() => setIsPinOpen(false)}
        onConfirm={confirmAttendance}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
